import fromPairs from 'lodash-es/fromPairs';
import _omit from 'lodash-es/omit';
import _pick from 'lodash-es/pick';
import set from 'lodash-es/set';

/**
 * Creates a new object from the list of key-value pairs.
 * Inverse of Object.entries().
 *
 * @param {Array[]} entries Array of key-value pairs (arrays).
 * @returns {Object} New object with the keys associated with the values.
 *
 * @example
 * > fromEntries([['foo', 1], ['bar', 2]])
 * { foo: 1, bar: 2 }
 */
export const fromEntries = fromPairs;

/**
 * Insert an key-value pair into an object.
 * Primarily used with Array.reduce().
 *
 * @param {Object} obj Target object for inserting the entry.
 * @param {Array} entry Key-value pair (array) to insert.
 * @returns {Object} Original object with the new entry inserted.
 *
 * @example
 * > [['foo', 1], ['bar', 2]].reduce(insertEntry, { alpha: 0 })
 * { alpha: 0, foo: 1, bar: 2 }
 */
export function insertEntry(obj, [key, value]) {
  return set(obj, key, value);
}

/**
 * Creates a new object from the passed object, including only the specified keys.
 * Ignores non-existent keys.
 *
 * @param {string[]} keys Keys to pick from the passed object.
 * @param {Object} object Object to pick keys from.
 * @returns {Object} New object with the picked keys only.
 *
 * @example
 * > pick(['a', 'c'], { a: null, b: null })
 * { a: null }
 */
export function pick(keys, object) {
  return _pick(object, keys);
}

/**
 * Creates a new object from the passed object, omitting the specified keys.
 * Ignores non-existent keys.
 *
 * @param {string[]} keys Keys to omit from the passed oject.
 * @param {Object} object Object to omit keys from.
 * @returns {Object} New object without the omitted keys.
 *
 * @example
 * > omit(['a', 'c'], { a: null, b: null })
 * { b: null }
 *
 * > omit([], { a: null, b: null })
 * { a: null, b: null }
 */
export function omit(keys, object) {
  return _omit(object, keys);
}

/**
 * Create a new object with the keys changed by the transform.
 *
 * @param {Function} transform Function mapping the old key to the new key.
 * @param {Object} object Object whose keys should change.
 * @returns {Object} New object with all the keys transformed.
 *
 * @example
 * > transformKeys(key => key.toUpperCase(), { a: 'foo', b: 'bar' })
 * { A: 'foo', B: 'bar' }
 */
export function transformKeys(transform, object) {
  return Object.entries(object)
    .map(([key, value]) => [transform(key), value])
    .reduce(insertEntry, {});
}

/**
 * Create a new object with the values changed by the transform.
 *
 * @param {Function} transform Function mapping the old value to the new value.
 * @param {Object} object Object whose values should change.
 * @returns {Object} New object with all the values transformed.
 *
 * @example
 * > transformValues(value => value.toUpperCase(), { a: 'foo', b: 'bar' })
 * { a: 'FOO', b: 'BAR' }
 */
export function transformValues(transform, object) {
  return Object.entries(object)
    .map(([key, value]) => [key, transform(value)])
    .reduce(insertEntry, {});
}
